import { useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import Layout from './components/Layout';
import Home from './components/Home';
import Blog from './pages/Blog';
import HysaRatesAugust2026 from './pages/HysaRatesAugust2026';
import NotFound from './pages/NotFound';
import { initAnalytics, trackPageView } from './lib/analytics';

// GA4 counts a page_view per document load, which for a client-side router is
// once per session. Report each route change instead — including the first,
// since initAnalytics configures gtag with send_page_view disabled.
//
// The dependency is the full location: /#rates and /#check are distinct
// destinations on the same pathname, and search params carry campaign tags.
function Analytics() {
  const { pathname, search, hash } = useLocation();

  useEffect(() => {
    initAnalytics();
    trackPageView(`${pathname}${search}${hash}`);
  }, [pathname, search, hash]);

  return null;
}

// The App Router restored scroll position on navigation and honoured #anchors in
// hrefs for free. A client-side router does neither: the browser resolves a hash
// before React has mounted the target section, so nav links like "/#rates"
// arriving from another route would land at the top of the page. Re-implement
// both behaviours here.
function ScrollBehaviour() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }
    // Defer a frame so the destination route has rendered its sections.
    const raf = requestAnimationFrame(() => {
      document.getElementById(hash.slice(1))?.scrollIntoView();
    });
    return () => cancelAnimationFrame(raf);
  }, [pathname, hash]);

  return null;
}

export default function App() {
  return (
    <>
      <ScrollBehaviour />
      <Analytics />
      <Layout>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/hysa-rates-august-2026" element={<HysaRatesAugust2026 />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Layout>
    </>
  );
}
